import { Tab, Checkbox } from "@blueprintjs/core";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import {
  AffixRequestApi,
  NewKennelRequestImportDto,
  KennelImportRequestImportDto,
  NameChangeRequestImporRequestDto,
  OwnershipChangeRequestImporRequestDto,
} from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { PageBase, ProtectedTabs } from "../../../../components";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { AffixImportPage } from "./tabs/AffixImportPage";
import { IDataTableColumn } from "nsitools-react";
export interface IFciImportPageProps {}

export const FciImportPage: React.FunctionComponent<IFciImportPageProps> = (props) => {
  const { t } = useTl();
  const { tab } = useParams<{ tab: string; state: string }>();
  const history = useHistory();
  const api = useApiService(AffixRequestApi);

  const newKennelRequestSave = React.useCallback(
    (items: NewKennelRequestImportDto[]) => api.saveNewKennelImport(items),
    [api]
  );

  const newKennelRequestImport = React.useCallback(
    async (files: any[]) => {
      if (files && files.length > 0) {
        return await api.computeNewKennelImports({ data: files[0].file, fileName: files[0].filename });
      }
      return null;
    },
    [api]
  );

  const importRequestSave = React.useCallback(
    (items: NewKennelRequestImportDto[]) => api.saveImportRequestImport(items),
    [api]
  );

  const importRequestImport = React.useCallback(
    async (files: any[]) => {
      if (files && files.length > 0) {
        return await api.computeImportRequestImports({ data: files[0].file, fileName: files[0].filename });
      }
      return null;
    },
    [api]
  );

  const nameChangeRequestSave = React.useCallback(
    (items: NameChangeRequestImporRequestDto[]) => api.saveNameChangeRequestImport(items),
    [api]
  );

  const nameChangeRequestImport = React.useCallback(
    async (files: any[]) => {
      if (files && files.length > 0) {
        return await api.computeNameChangeRequestImport({ data: files[0].file, fileName: files[0].filename });
      }
      return null;
    },
    [api]
  );

  const ownershipChangeRequestSave = React.useCallback(
    (items: OwnershipChangeRequestImporRequestDto[]) => api.saveOwnershipChangeRequestImport(items),
    [api]
  );

  const ownershipChangeRequestImport = React.useCallback(
    async (files: any[]) => {
      if (files && files.length > 0) {
        return await api.computeOwnershipChangeRequestImport({ data: files[0].file, fileName: files[0].filename });
      }
      return null;
    },
    [api]
  );

  const columnsNewKennel = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        fieldName: "requestNumber",
        header: () => t(ETLCodes.RequestNumber),
      },
      {
        fieldName: "finalName",
        header: () => t(ETLCodes.FinalName),
      },
      {
        fieldName: "finalType",
        header: () => t(ETLCodes.FinalType),
      },
      {
        fieldName: "importable",
        header: () => t(ETLCodes.Importable),
        render: (row: NameChangeRequestImporRequestDto) => <Checkbox disabled checked={row.importable} />,
      },
      {
        fieldName: "accepted",
        header: () => t(ETLCodes.Accepted),
        render: (row: NameChangeRequestImporRequestDto) => <Checkbox disabled checked={row.accepted} />,
      },
    ],
    [t]
  );

  const columnsNameChange = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        fieldName: "requestNumber",
        header: () => t(ETLCodes.RequestNumber),
      },
      {
        fieldName: "fciNumber",
        header: () => t(ETLCodes.FciNumber),
      },
      {
        fieldName: "oldKennelName",
        header: () => t(ETLCodes.PreviousKennel),
      },
      {
        fieldName: "oldAffix",
        header: () => t(ETLCodes.PreviousAffix),
      },
      {
        fieldName: "finalName",
        header: () => t(ETLCodes.FinalName),
      },
      {
        fieldName: "finalType",
        header: () => t(ETLCodes.FinalAffix),
      },
      {
        fieldName: "importable",
        header: () => t(ETLCodes.Importable),
        render: (row: KennelImportRequestImportDto) => <Checkbox disabled checked={row.importable} />,
      },
      {
        fieldName: "accepted",
        header: () => t(ETLCodes.Accepted),
        render: (row: KennelImportRequestImportDto) => <Checkbox disabled checked={row.accepted} />,
      },
    ],
    [t]
  );

  const columnsImport = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        fieldName: "requestNumber",
        header: () => t(ETLCodes.RequestNumber),
      },
      {
        fieldName: "fciNumber",
        header: () => t(ETLCodes.FciNumber),
      },
      {
        fieldName: "originCountry",
        header: () => t(ETLCodes.Country),
      },
      {
        fieldName: "kennelName",
        header: () => t(ETLCodes.Name),
      },
      {
        fieldName: "affix",
        header: () => t(ETLCodes.Affix),
      },
      {
        fieldName: "importable",
        header: () => t(ETLCodes.Importable),
        render: (row: KennelImportRequestImportDto) => <Checkbox disabled checked={row.importable} />,
      },
      {
        fieldName: "accepted",
        header: () => t(ETLCodes.Accepted),
        render: (row: KennelImportRequestImportDto) => <Checkbox disabled checked={row.accepted} />,
      },
    ],
    [t]
  );

  const columnsOwnershipChange = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        fieldName: "requestNumber",
        header: () => t(ETLCodes.RequestNumber),
      },
      {
        fieldName: "kennelName",
        header: () => t(ETLCodes.Name),
      },
      {
        fieldName: "affix",
        header: () => t(ETLCodes.Affix),
      },
      {
        fieldName: "importable",
        header: () => t(ETLCodes.Importable),
        render: (row: KennelImportRequestImportDto) => <Checkbox disabled checked={row.importable} />,
      },
      {
        fieldName: "accepted",
        header: () => t(ETLCodes.Accepted),
        render: (row: KennelImportRequestImportDto) => <Checkbox disabled checked={row.accepted} />,
      },
    ],
    [t]
  );

  return (
    <PageBase breadCrumbs={[{ text: t(ETLCodes.FCIAffixesImport), route: ERoutes.affixImport }]}>
      <ProtectedTabs
        id="importTabs"
        onChange={(newTabId) => history.push(`${ERoutes.affixImport}/${newTabId}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <Tab
          id="newKennel"
          title={t(ETLCodes.NewKennel)}
          panel={
            <AffixImportPage
              importFunction={newKennelRequestImport}
              saveFunction={newKennelRequestSave}
              columns={columnsNewKennel}
            />
          }
        />
        <Tab
          id="import"
          title={t(ETLCodes.ImportKennelRequest)}
          panel={
            <AffixImportPage
              importFunction={importRequestImport}
              saveFunction={importRequestSave}
              columns={columnsImport}
            />
          }
        />
        <Tab
          id="nameChange"
          title={t(ETLCodes.KennelNameChangeRequest)}
          panel={
            <AffixImportPage
              columns={columnsNameChange}
              saveFunction={nameChangeRequestSave}
              importFunction={nameChangeRequestImport}
            />
          }
        />
        <Tab
          id="ownershipChange"
          title={t(ETLCodes.KennelOwnershipChangeRequest)}
          panel={
            <AffixImportPage
              columns={columnsOwnershipChange}
              saveFunction={ownershipChangeRequestSave}
              importFunction={ownershipChangeRequestImport}
            />
          }
        />
      </ProtectedTabs>
    </PageBase>
  );
};
